import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Avatar,
  CircularProgress,
  Grid,
  InputAdornment,
  Paper,
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { useCreateCustomerMutation } from "../../../Api/attoDeskApi";
import axios from "axios";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router";
import {
  validateEmail,
  validatePhoneNumber,
  formatPhoneNumber,
  requiredValidateEmail,
  requiredValidatePhoneNumber,
} from "../../../Components/Validation/validationUtils";
import { uploadImage } from "../../../Components/UploadFile/upload";

// ── Constants outside component so they are never recreated ──────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13, bgcolor: "#fff",
    "&:hover fieldset": { borderColor: "#2f3891" },
    "&.Mui-focused fieldset": { borderColor: "#2f3891", borderWidth: 2 },
  },
};

const labelSx = { fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.5, display: "block" };

// ── Canadian provinces ────────────────────────────────────────────────────────
const CANADIAN_PROVINCES = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

const PROVINCE_NAME_TO_CODE: Record<string, string> = {
  Alberta: "AB", "British Columbia": "BC", Manitoba: "MB",
  "New Brunswick": "NB", "Newfoundland and Labrador": "NL",
  "Nova Scotia": "NS", "Northwest Territories": "NT",
  Nunavut: "NU", Ontario: "ON", "Prince Edward Island": "PE",
  Quebec: "QC", Saskatchewan: "SK", Yukon: "YT",
};

interface NominatimResult {
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    county?: string;
    state?: string;
    postcode?: string;
  };
}

const TIME_SLOTS = [
  "Morning (8:00 AM – 10:00 AM)",
  "Late Morning (10:00 AM – 12:00 PM)",
  "Afternoon (12:00 PM – 2:00 PM)",
  "Mid Afternoon (2:00 PM – 4:00 PM)",
  "Late Afternoon (4:00 PM – 6:00 PM)",
  "Evening (6:00 PM – 8:00 PM)",
  "Anytime",
  "Weekdays Only",
  "Weekends Only",
];

// ── SectionCard outside component so React never remounts it ─────────────────
const SectionCard = ({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => (
  <Box
    sx={{
      bgcolor: "#fff", borderRadius: 3,
      border: "1px solid #e2e8f0",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      overflow: "hidden",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
      <Box
        sx={{
          width: 32, height: 32, borderRadius: 1.5,
          background: "linear-gradient(135deg,#2f3891,#2f3891)",
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 3 }}>{children}</Box>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const Index = () => {
  const navigate = useNavigate();
  const [newCustomer, { isLoading }] = useCreateCustomerMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [isImageUploading, setIsImageUploading] = useState(false);

  // Duplicate phone modal
  const [dupClient, setDupClient] = useState<{ id: number; name: string; phone: string; email: string } | null>(null);

  // ── Per-field duplicate validation (real-time) ────────────────────────────
  const API_URL = process.env.REACT_APP_API_URL;
  const [dupErrors, setDupErrors] = useState<Record<string, string>>({});
  const dupTimers = React.useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

  const setDupError = (field: string, msg: string) => {
    setDupErrors((prev) => {
      if (msg) return { ...prev, [field]: msg };
      const next = { ...prev }; delete next[field]; return next;
    });
  };

  const checkPhoneDuplicate = (field: string, phone: string) => {
    if (dupTimers.current[field]) clearTimeout(dupTimers.current[field]!);
    const trimmed = phone.trim();
    if (!trimmed) { setDupError(field, ""); return; }
    dupTimers.current[field] = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/customer/check-phone`, {
          params: { phone: trimmed },
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        if (res.data?.exists) {
          const name = res.data?.client?.name || "another client";
          setDupError(field, `Phone number already in use by ${name}`);
        } else {
          setDupError(field, "");
        }
      } catch { /* silent */ }
    }, 450);
  };

  const checkEmailDuplicate = (field: string, email: string) => {
    if (dupTimers.current[field]) clearTimeout(dupTimers.current[field]!);
    const trimmed = email.trim();
    if (!trimmed) { setDupError(field, ""); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setDupError(field, ""); return; }
    dupTimers.current[field] = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/customer/check-email`, {
          params: { email: trimmed },
          headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
        });
        if (res.data?.exists) {
          const name = res.data?.client?.name || "another client";
          setDupError(field, `Email already in use by ${name}`);
        } else {
          setDupError(field, "");
        }
      } catch { /* silent */ }
    }, 450);
  };

  const hasAnyDupError = () => Object.values(dupErrors).some((m) => !!m);

  // ── Address autocomplete state ─────────────────────────────────────────────
  const [addrSuggestions, setAddrSuggestions] = useState<Array<{ street: string; city: string; province: string; postalCode: string; display: string }>>([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const addrTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddress = (val: string) => {
    formik.setFieldValue("customerStreetName", val);
    if (addrTimer.current) clearTimeout(addrTimer.current);
    if (val.trim().length < 4) { setAddrSuggestions([]); setAddrOpen(false); return; }
    setAddrOpen(true);
    addrTimer.current = setTimeout(async () => {
      setAddrLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(val)}&countrycodes=ca&addressdetails=1&limit=6`;
        const res = await fetch(url, { headers: { "Accept-Language": "en" } });
        const data: NominatimResult[] = await res.json();
        setAddrSuggestions(data.map(item => ({
          street: [item.address?.house_number, item.address?.road].filter(Boolean).join(" ") || item.display_name.split(",")[0],
          city: item.address?.city || item.address?.town || item.address?.village || item.address?.county || "",
          province: PROVINCE_NAME_TO_CODE[item.address?.state || ""] || item.address?.state || "",
          postalCode: item.address?.postcode || "",
          display: item.display_name,
        })));
      } catch { setAddrSuggestions([]); } finally { setAddrLoading(false); }
    }, 450);
  };

  const applyAddress = (s: { street: string; city: string; province: string; postalCode: string }) => {
    const parts = s.street.split(" ");
    const streetNum = /^\d+/.test(parts[0]) ? parts[0] : "";
    const streetName = streetNum ? parts.slice(1).join(" ") : s.street;
    formik.setFieldValue("customerStreetNumber", streetNum);
    formik.setFieldValue("customerStreetName", streetName);
    formik.setFieldValue("customerCity", s.city);
    formik.setFieldValue("customerProvince", s.province);
    formik.setFieldValue("customerPostalCode", s.postalCode);
    setAddrSuggestions([]);
    setAddrOpen(false);
  };

  const previewSrc = React.useMemo(
    () => (selectedImage ? URL.createObjectURL(selectedImage) : null),
    [selectedImage],
  );

  // ── Image resize ──────────────────────────────────────────────────────────
  const resizeImage = (file: File, maxW: number, maxH: number): Promise<File> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let { width, height } = img;
          if (width > height) { if (width > maxW) { height *= maxW / width; width = maxW; } }
          else { if (height > maxH) { width *= maxH / height; height = maxH; } }
          canvas.width = width; canvas.height = height;
          canvas.getContext("2d")?.drawImage(img, 0, 0, width, height);
          canvas.toBlob(
            (blob) => blob
              ? resolve(new File([blob], file.name, { type: file.type }))
              : reject(new Error("Canvas empty")),
            file.type,
          );
        };
        img.onerror = reject;
      };
    });

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) { setSelectedImage(undefined); return; }
    const valid = ["image/jpeg", "image/png", "image/jpg", "image/gif"];
    if (!valid.includes(file.type)) { showErrorMessage("Image must be jpeg, png, jpg or gif."); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("Image must be smaller than 2 MB."); return; }
    try { setSelectedImage(await resizeImage(file, 500, 500)); }
    catch { showErrorMessage("Failed to process image."); }
  };

  // ── Formik ────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      customerCompanyName: "",
      customerCompanyPhoneNumebr: "",
      customerCompanyEmail: "",
      customerPreferTimeToCall: "",
      customerFirstName: "",
      customerLastName: "",
      customerMobileNum: "",
      customerEmail: "",
      customerDescription: "",
      customerProvince: "",
      customerCity: "",
      customerPostalCode: "",
      customerStreetNumber: "",
      customerStreetName: "",
      customerImg: "",
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.customerCompanyName.trim()) errors.customerCompanyName = "Name is required";
      const companyEmailErr = validateEmail(values.customerCompanyEmail);
      if (companyEmailErr) errors.customerCompanyEmail = companyEmailErr;
      const companyPhoneErr = validatePhoneNumber(values.customerCompanyPhoneNumebr);
      if (companyPhoneErr) errors.customerCompanyPhoneNumebr = companyPhoneErr;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      if (hasAnyDupError()) {
        const firstErr = Object.values(dupErrors).find((m) => !!m);
        if (firstErr) showErrorMessage(firstErr);
        return;
      }
      try {
        let customerImg: string | undefined;
        if (selectedImage) {
          setIsImageUploading(true);
          customerImg = (await uploadImage(selectedImage)) || "";
          setIsImageUploading(false);
        }
        const res = await newCustomer({
          customerFirstName: values.customerFirstName,
          customerLastName: values.customerLastName,
          customerMobileNum: values.customerMobileNum,
          customerEmail: values.customerEmail,
          customerProvince: values.customerProvince,
          customerCity: values.customerCity,
          customerPostalCode: values.customerPostalCode,
          customerStreetNumber: values.customerStreetNumber,
          customerStreetName: values.customerStreetName,
          customerDescription: values.customerDescription,
          customerCompanyName: values.customerCompanyName,
          customerCompanyPhoneNumebr: values.customerCompanyPhoneNumebr,
          customerCompanyEmail: values.customerCompanyEmail,
          customerPreferTimeToCall: values.customerPreferTimeToCall,
          customerImg,
        }).unwrap();

        if (!res.status) {
          if ((res as any).field === "customerCompanyPhoneNumebr") {
            formik.setFieldError("customerCompanyPhoneNumebr", "Phone number already in use");
            formik.setFieldTouched("customerCompanyPhoneNumebr", true, false);
            if ((res as any).existingClient) {
              setDupClient((res as any).existingClient);
            } else {
              showErrorMessage(res.message);
            }
          } else {
            showErrorMessage(res.message);
          }
        } else {
          showMessage("Client created successfully");
          resetForm();
          setSelectedImage(undefined);
          const newId = (res as any)?.data?.id;
          if (newId) {
            navigate(`/customer-history/${newId}`);
          } else {
            navigate("/customer/list");
          }
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const initials = [formik.values.customerFirstName[0], formik.values.customerLastName[0]]
    .filter(Boolean).join("").toUpperCase() || "?";

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, maxWidth: 1100, mx: "auto" }}>

      {/* ── Page header ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            width: 40, height: 40, borderRadius: 2,
            background: "linear-gradient(135deg,#2f3891,#2f3891)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <PersonAddAltOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", lineHeight: 1 }}>
            Create Client
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
            Add a new client to your directory
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

        {/* ── Section 1: Company / School / Person ─────────────────────────── */}
        <SectionCard
          icon={<BusinessOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />}
          title="Company / School / Person Information"
        >
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Name <span style={{ color: "#ef4444" }}>*</span></Typography>
              <TextField
                fullWidth size="small" placeholder="Enter Name"
                {...formik.getFieldProps("customerCompanyName")}
                error={formik.touched.customerCompanyName && Boolean(formik.errors.customerCompanyName)}
                helperText={formik.touched.customerCompanyName && formik.errors.customerCompanyName}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Phone Number</Typography>
              <TextField
                fullWidth size="small" placeholder="(416) 555-0100"
                {...formik.getFieldProps("customerCompanyPhoneNumebr")}
                onChange={(e) => {
                  const val = formatPhoneNumber(e.target.value);
                  formik.setFieldValue("customerCompanyPhoneNumebr", val);
                  if (formik.errors.customerCompanyPhoneNumebr) {
                    formik.setFieldError("customerCompanyPhoneNumebr", undefined);
                  }
                  checkPhoneDuplicate("companyPhone", val);
                }}
                error={!!dupErrors.companyPhone || (formik.touched.customerCompanyPhoneNumebr && Boolean(formik.errors.customerCompanyPhoneNumebr))}
                helperText={dupErrors.companyPhone || (formik.touched.customerCompanyPhoneNumebr && formik.errors.customerCompanyPhoneNumebr)}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>E-mail</Typography>
              <TextField
                fullWidth size="small" type="email" placeholder="Enter E-mail"
                value={formik.values.customerCompanyEmail}
                onChange={(e) => {
                  formik.setFieldValue("customerCompanyEmail", e.target.value);
                  checkEmailDuplicate("companyEmail", e.target.value);
                }}
                onBlur={formik.handleBlur}
                name="customerCompanyEmail"
                error={!!dupErrors.companyEmail || (formik.touched.customerCompanyEmail && Boolean(formik.errors.customerCompanyEmail))}
                helperText={dupErrors.companyEmail || (formik.touched.customerCompanyEmail && formik.errors.customerCompanyEmail)}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>Prefer Time to call</Typography>
              <Select
                fullWidth size="small" displayEmpty
                {...formik.getFieldProps("customerPreferTimeToCall")}
                sx={{
                  borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891", borderWidth: 2 },
                }}
              >
                <MenuItem value="" disabled sx={{ fontSize: 13, color: "#94a3b8" }}>
                  Select preferred time
                </MenuItem>
                {TIME_SLOTS.map((slot) => (
                  <MenuItem key={slot} value={slot} sx={{ fontSize: 13 }}>{slot}</MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </SectionCard>

        {/* ── Section 2: Contact Information ────────────────────────────────── */}
        <SectionCard
          icon={<PersonOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />}
          title="Contact Information"
        >
          <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
              All fields in this section are optional. Fill in contact details if available.
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>
                Name <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth size="small" placeholder="Enter Name"
                {...formik.getFieldProps("customerLastName")}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>
                Mobile Number <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth size="small" placeholder="(416) 555-0100"
                {...formik.getFieldProps("customerMobileNum")}
                onChange={(e) => {
                  const val = formatPhoneNumber(e.target.value);
                  formik.setFieldValue("customerMobileNum", val);
                  checkPhoneDuplicate("mobilePhone", val);
                }}
                error={!!dupErrors.mobilePhone || (formik.touched.customerMobileNum && Boolean(formik.errors.customerMobileNum))}
                helperText={dupErrors.mobilePhone || (formik.touched.customerMobileNum && formik.errors.customerMobileNum)}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography sx={labelSx}>
                E-mail <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth size="small" type="email" placeholder="Enter E-mail"
                value={formik.values.customerEmail}
                onChange={(e) => {
                  formik.setFieldValue("customerEmail", e.target.value);
                  checkEmailDuplicate("contactEmail", e.target.value);
                }}
                onBlur={formik.handleBlur}
                name="customerEmail"
                error={!!dupErrors.contactEmail || (formik.touched.customerEmail && Boolean(formik.errors.customerEmail))}
                helperText={dupErrors.contactEmail || (formik.touched.customerEmail && formik.errors.customerEmail)}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography sx={labelSx}>
                Notes <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth size="small" multiline rows={5}
                placeholder="Enter notes…"
                {...formik.getFieldProps("customerDescription")}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </SectionCard>

        {/* ── Section 3: Address Information ───────────────────────────────── */}
        <SectionCard
          icon={<LocationOnOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />}
          title="Address Information"
        >
          <Box sx={{ mb: 2, px: 1.5, py: 1, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #e2e8f0" }}>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>
              All fields in this section are optional. Start typing in Address to get suggestions.
            </Typography>
          </Box>
          <Grid container spacing={2.5}>
            {/* Street Address with Nominatim autocomplete */}
            <Grid item xs={12} sm={8}>
              <Typography sx={labelSx}>
                Address <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <Box sx={{ position: "relative" }}>
                <TextField
                  fullWidth size="small"
                  placeholder="Start typing street address…"
                  value={`${formik.values.customerStreetNumber ? formik.values.customerStreetNumber + " " : ""}${formik.values.customerStreetName}`}
                  onChange={(e) => searchAddress(e.target.value)}
                  onFocus={() => { if (addrSuggestions.length > 0) setAddrOpen(true); }}
                  onBlur={() => setTimeout(() => setAddrOpen(false), 200)}
                  InputProps={{
                    endAdornment: addrLoading
                      ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment>
                      : undefined,
                    sx: { bgcolor: "#fff" },
                  }}
                  sx={fieldSx}
                />
                {addrOpen && addrSuggestions.length > 0 && (
                  <Paper sx={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999,
                    maxHeight: 240, overflowY: "auto", border: "1px solid #e2e8f0",
                    borderRadius: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", mt: 0.5,
                  }}>
                    <Box sx={{ px: 1.5, py: 0.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Address Suggestions
                      </Typography>
                    </Box>
                    {addrSuggestions.map((s, i) => (
                      <Box
                        key={i}
                        onMouseDown={() => applyAddress(s)}
                        sx={{ px: 2, py: 1, cursor: "pointer", "&:hover": { bgcolor: "#eff6ff" }, borderBottom: "1px solid #f1f5f9" }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{s.street}</Typography>
                        <Typography sx={{ fontSize: 11, color: "#64748b" }} noWrap>{s.display}</Typography>
                      </Box>
                    ))}
                  </Paper>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography sx={labelSx}>
                Street No. <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth size="small" placeholder="e.g. 123"
                {...formik.getFieldProps("customerStreetNumber")}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography sx={labelSx}>
                City <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth size="small" placeholder="City"
                {...formik.getFieldProps("customerCity")}
                sx={fieldSx}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography sx={labelSx}>
                Province <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <Select
                fullWidth size="small" displayEmpty
                {...formik.getFieldProps("customerProvince")}
                sx={{
                  borderRadius: 2, fontSize: 13, bgcolor: "#fff",
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#2f3891", borderWidth: 2 },
                }}
              >
                <MenuItem value="" sx={{ fontSize: 13, color: "#94a3b8" }}>Select Province</MenuItem>
                {CANADIAN_PROVINCES.map((p) => (
                  <MenuItem key={p.code} value={p.code} sx={{ fontSize: 13 }}>{p.name}</MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={4}>
              <Typography sx={labelSx}>
                Postal Code <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: "#94a3b8" }}>(optional)</Box>
              </Typography>
              <TextField
                fullWidth size="small" placeholder="e.g. M1A 1A1"
                {...formik.getFieldProps("customerPostalCode")}
                onChange={(e) => formik.setFieldValue("customerPostalCode", e.target.value.toUpperCase())}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </SectionCard>

        {/* ── Section 4: Image ──────────────────────────────────────────────── */}
        <Box
          sx={{
            bgcolor: "#fff", borderRadius: 3,
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f1f5f9" }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>Image</Typography>
          </Box>
          <Box sx={{ p: 3, display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
            {previewSrc ? (
              <Box
                component="img" src={previewSrc} alt="preview"
                sx={{ width: 80, height: 80, borderRadius: "50%", objectFit: "cover", border: "3px solid #e2e8f0", flexShrink: 0 }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 80, height: 80, fontSize: 24, fontWeight: 700, flexShrink: 0,
                  background: "linear-gradient(135deg,#2f3891,#2f3891)",
                }}
              >
                {initials}
              </Avatar>
            )}
            <Box>
              <input
                accept="image/*" style={{ display: "none" }}
                id="customer-img-upload" type="file"
                onChange={handleImageChange}
              />
              <label htmlFor="customer-img-upload">
                <Button
                  component="span" variant="outlined"
                  startIcon={<PhotoCameraOutlinedIcon />}
                  sx={{
                    borderRadius: 2, textTransform: "none", fontSize: 13, fontWeight: 600,
                    borderColor: "#e2e8f0", color: "#64748b",
                    "&:hover": { borderColor: "#2f3891", color: "#2f3891", bgcolor: "#f0f0ff" },
                  }}
                >
                  {selectedImage ? "Change Photo" : "Upload Photo"}
                </Button>
              </label>
              {selectedImage && (
                <Button
                  size="small"
                  onClick={() => setSelectedImage(undefined)}
                  sx={{ ml: 1, textTransform: "none", fontSize: 12, color: "#ef4444", borderRadius: 2 }}
                >
                  Remove
                </Button>
              )}
              <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75 }}>
                JPEG, PNG, JPG or GIF — max 2 MB
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Duplicate phone modal ─────────────────────────────────────────── */}
        <Dialog
          open={!!dupClient}
          onClose={() => setDupClient(null)}
          maxWidth="xs" fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
        >
          <Box sx={{ height: 4, bgcolor: "#f59e0b" }} />
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, pb: 1 }}>
            <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: "#d97706" }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a" }}>
              Duplicate Phone Number
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ pt: 0 }}>
            <Typography sx={{ fontSize: 13, color: "#64748b", mb: 2 }}>
              This phone number is already used by another client:
            </Typography>
            {dupClient && (
              <Box sx={{ bgcolor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 2, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PersonOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{dupClient.name}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PhoneOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
                  <Typography sx={{ fontSize: 13, color: "#475569" }}>{dupClient.phone}</Typography>
                </Box>
                {dupClient.email && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
                    <Typography sx={{ fontSize: 13, color: "#475569" }}>{dupClient.email}</Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setDupClient(null)}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 1.5, borderColor: "#e2e8f0", color: "#64748b", "&:hover": { borderColor: "#94a3b8" } }}
            >
              Enter New Number
            </Button>
            <Button
              variant="contained"
              endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate(`/customer-history/${dupClient?.id}`)}
              sx={{ textTransform: "none", fontWeight: 600, fontSize: 13, borderRadius: 1.5, background: "linear-gradient(135deg,#2f3891,#2f3891)", boxShadow: "none" }}
            >
              Go to Client Profile
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Action bar ────────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", pb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/customer/list")}
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13,
              borderColor: "#e2e8f0", color: "#64748b",
              "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={(isLoading || isImageUploading)
              ? <CircularProgress size={14} sx={{ color: "#fff" }} />
              : <SaveAltIcon />}
            onClick={() => formik.handleSubmit()}
            disabled={isLoading || isImageUploading}
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13,
              background: "linear-gradient(135deg,#2f3891,#2f3891)",
              boxShadow: "0 2px 8px rgba(99,102,241,0.35)", px: 3,
              "&:hover": { background: "linear-gradient(135deg,#2f3891,#2f3891)" },
              "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
            }}
          >
            Save Client
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Index;
